<template>
  <div>
    <!-- App Bar -->
    <v-app-bar color="transparent" class="modern-app-bar" app flat :elevation="0" :class="{ 'scrolled': scrolled }">
      <!-- Logo y marca -->
      <div class="brand-section" @click="$router.push('/')">
        <div class="logo-container">
          <div class="logo-box">
            <img :src="urlImagen(logo)" alt="Logo" class="logo-image" v-if="logo">
            <v-icon v-else class="logo-fallback" color="#F2F2ED" size="20">mdi-dumbbell</v-icon>
          </div>
          <div class="brand-text hidden-sm-and-down">
            <div class="gym-name">{{ nombreGimnasio }}</div>
            <div class="gym-tagline">Gym OS</div>
          </div>
        </div>
      </div>

      <!-- Navegación rápida desktop -->
      <div class="desktop-quick-nav hidden-sm-and-down">
        <v-btn v-for="item in quickNavItemsFiltrados" :key="item.title" :to="item.link" text class="quick-nav-btn"
          :class="{ 'active-nav': item.active }" @click="setActiveQuickNav(item.title)">
          {{ item.title }}
        </v-btn>
      </div>

      <v-spacer></v-spacer>

      <!-- Acciones del usuario -->
      <div class="user-actions">
        <!-- Notificaciones -->
        <v-menu offset-y class="hidden-sm-and-down">
          <template v-slot:activator="{ on, attrs }">
            <v-btn icon v-bind="attrs" v-on="on" class="action-btn">
              <v-badge color="#C8FF4D" :content="notifications" :value="notifications" offset-x="10" offset-y="10" dot-color="#0E0E10">
                <v-icon color="#8C8C93" size="20">mdi-bell-outline</v-icon>
              </v-badge>
            </v-btn>
          </template>
          <v-card class="dropdown-card">
            <div class="dropdown-head">Notificaciones</div>
            <v-divider class="dropdown-divider"></v-divider>
            <div class="dropdown-item" v-for="n in notificationItems" :key="n.id">
              <v-icon size="16" color="#8C8C93" class="mr-3">{{ n.icon }}</v-icon>
              <div>
                <div class="dropdown-item-title">{{ n.title }}</div>
                <div class="dropdown-item-time">{{ n.time }}</div>
              </div>
            </div>
          </v-card>
        </v-menu>

        <!-- Perfil -->
        <div class="hidden-sm-and-down">
          <v-menu offset-y>
            <template v-slot:activator="{ on, attrs }">
              <v-btn text v-bind="attrs" v-on="on" class="user-profile-btn">
                <span class="user-avatar">
                  <v-icon size="16" color="#0E0E10">mdi-account</v-icon>
                </span>
                <span class="username">{{ nombreUsuario }}</span>
                <v-icon size="14" color="#8C8C93">mdi-chevron-down</v-icon>
              </v-btn>
            </template>
            <v-card class="dropdown-card dropdown-card-narrow">
              <v-list-item link :to="'/perfil'" class="dropdown-link">
                <v-icon size="16" class="mr-3" color="#8C8C93">mdi-account-cog</v-icon>
                <span>Mi Perfil</span>
              </v-list-item>
              <v-list-item link :to="'/configurar'" class="dropdown-link">
                <v-icon size="16" class="mr-3" color="#8C8C93">mdi-cog</v-icon>
                <span>Configuración</span>
              </v-list-item>
              <v-divider class="dropdown-divider"></v-divider>
              <v-list-item @click="salir" class="dropdown-link dropdown-link-danger">
                <v-icon size="16" class="mr-3" color="#FF6B6B">mdi-logout</v-icon>
                <span>Cerrar Sesión</span>
              </v-list-item>
            </v-card>
          </v-menu>
        </div>

        <!-- Menú hamburguesa móvil -->
        <v-app-bar-nav-icon @click.stop="toggleDrawer" class="nav-icon-mobile hidden-md-and-up">
          <v-icon color="#F2F2ED">mdi-menu</v-icon>
        </v-app-bar-nav-icon>

        <!-- Toggle drawer escritorio -->
        <v-btn icon @click.stop="toggleDrawer" class="drawer-toggle-btn hidden-sm-and-down">
          <v-icon size="20" color="#8C8C93">{{ drawer ? 'mdi-close' : 'mdi-menu' }}</v-icon>
        </v-btn>
      </div>
    </v-app-bar>

    <!-- Drawer -->
    <v-navigation-drawer v-model="drawer" class="modern-drawer" :temporary="true" right :width="drawerWidth" app>
      <!-- Header del drawer -->
      <div class="drawer-header">
        <div class="drawer-user-info">
          <v-avatar size="56" class="drawer-avatar">
            <v-img :src="urlImagen(logo)" v-if="logo">
              <template v-slot:placeholder>
                <v-icon color="#0E0E10" size="24">mdi-dumbbell</v-icon>
              </template>
            </v-img>
            <v-icon v-else color="#0E0E10" size="24">mdi-dumbbell</v-icon>
          </v-avatar>
          <div class="user-details">
            <h3 class="gym-name-drawer">{{ nombreGimnasio }}</h3>
            <div class="user-role">{{ nombreUsuario }}</div>
            <div class="status-pill">
              <span class="status-dot"></span>
              En línea
            </div>
          </div>
        </div>

        <!-- Acciones rápidas -->
        <div class="quick-actions">
          <button class="quick-action-btn" :to="'/scanner'" @click="$router.push('/scanner'); closeDrawer()">
            <v-icon size="16" class="mr-2">mdi-qrcode-scan</v-icon>
            Escanear QR
          </button>
          <button class="quick-action-btn" @click="$router.push('/registrar-visita'); closeDrawer()">
            <v-icon size="16" class="mr-2">mdi-account-plus</v-icon>
            Registrar Visita
          </button>
        </div>
      </div>

      <!-- Menú de navegación -->
      <v-list dense nav class="modern-nav-list">
        <template v-for="(section, index) in menuSectionsFiltradas">
          <div :key="`div-${index}`" v-if="index > 0" class="section-divider"></div>

          <v-list-group :key="section.title" v-model="section.open" class="modern-section-group"
            active-class="active-section" no-action>
            <template v-slot:activator>
              <v-list-item-content>
                <v-list-item-title class="section-title">{{ section.title }}</v-list-item-title>
              </v-list-item-content>
            </template>

            <v-list-item v-for="item in section.items" :key="item.title" :to="item.link" link
              class="modern-menu-item" active-class="active-menu-item" @click="closeDrawer">
              <v-list-item-icon>
                <div class="menu-icon">
                  <v-icon size="15">{{ item.icon }}</v-icon>
                </div>
              </v-list-item-icon>
              <v-list-item-content>
                <v-list-item-title class="menu-item-title">{{ item.title }}</v-list-item-title>
              </v-list-item-content>
              <v-list-item-action v-if="item.badge">
                <span class="menu-badge">{{ item.badge }}</span>
              </v-list-item-action>
            </v-list-item>
          </v-list-group>
        </template>
      </v-list>

      <!-- Footer del drawer -->
      <div class="drawer-footer">
        <div class="footer-divider"></div>
        <div class="footer-content">
          <span class="system-text">Bamboo v2.0.0</span>
          <button class="footer-icon-btn" @click="toggleTheme">
            <v-icon size="15" color="#8C8C93">mdi-theme-light-dark</v-icon>
          </button>
        </div>
        <button class="logout-btn" @click="salir">
          <v-icon size="16" class="mr-2">mdi-logout</v-icon>
          Cerrar Sesión
        </button>
      </div>
    </v-navigation-drawer>

    <!-- Overlay móvil -->
    <v-overlay :value="drawer && $vuetify.breakpoint.smAndDown" opacity="0.5" @click="drawer = false"
      z-index="5"></v-overlay>
  </div>
</template>

<script>
import Utiles from '../Servicios/Utiles'

export default {
  name: "EncabezadoModerno",

  data: () => ({
    drawer: false,
    scrolled: false,
    nombreUsuario: "",
    nombreGimnasio: "",
    logo: "",
    notifications: 3,

    quickNavItems: [
      { title: "Dashboard", icon: "mdi-view-dashboard", link: "/", active: true, permiso: "dashboard" },
      { title: "Visita", icon: "mdi-account-arrow-right", link: "/registrar-visita", active: false, permiso: "visitas" },
      { title: "Miembros", icon: "mdi-account-group", link: "/miembros", active: false, permiso: "miembros" },
      { title: "Clases", icon: "mdi-calendar-clock", link: "/gestion-clases", active: false, permiso: "clases" },
      { title: "Venta", icon: "mdi-cash-register", link: "/productos", active: false, permiso: "reportes_ventas" },
    ],

    notificationItems: [
      { id: 1, title: "Notificaciones", icon: "mdi-account-plus", time: "Proximamente" },
    ],

    menuSections: [
      {
        title: "Principal",
        icon: "mdi-home",
        open: true,
        items: [
          { title: "Dashboard", icon: "mdi-view-dashboard-outline", link: "/", badge: null, permiso: "dashboard" },
          { title: "Registrar Visita", icon: "mdi-account-arrow-right", link: "/registrar-visita", badge: "New", permiso: "visitas" },
          { title: "Scanner QR", icon: "mdi-qrcode-scan", link: "/scanner", badge: null, permiso: "scanner" },
        ]
      },
      {
        title: "Gestión de Miembros",
        icon: "mdi-account-group",
        open: false,
        items: [
          { title: "Usuarios", icon: "mdi-account-box-outline", link: "/usuarios", badge: null, permiso: "usuarios" },
          { title: "Miembros", icon: "mdi-account-details", link: "/miembros", badge: null, permiso: "miembros" },
          { title: "Membresías", icon: "mdi-card-account-details", link: "/membresias", badge: null, permiso: "membresias" },
        ]
      },
      {
        title: "Entrenamiento",
        icon: "mdi-dumbbell",
        open: false,
        items: [
          { title: "Clases", icon: "mdi-calendar-clock", link: "/gestion-clases", badge: null, permiso: "clases" },
          { title: "Rutinas", icon: "mdi-clipboard-list", link: "/lista-rutinas", badge: null, permiso: "rutinas" },
          { title: "Ejercicios", icon: "mdi-weight-lifter", link: "/ejercicios", badge: null, permiso: "ejercicios" },
          { title: "Asignaciones", icon: "mdi-clipboard-check", link: "/gestion-asignaciones", badge: null, permiso: "gestion_asignaciones" },
        ]
      },
      {
        title: "Comercio",
        icon: "mdi-store",
        open: false,
        items: [
          { title: "Productos", icon: "mdi-package-variant", link: "/productos", badge: null, permiso: "productos" },
          { title: "Ventas", icon: "mdi-cash-register", link: "/reporte-ventas", badge: null, permiso: "reportes_ventas" },
        ]
      },
      {
        title: "Analítica",
        icon: "mdi-chart-box",
        open: false,
        items: [
          { title: "Reportes de Pagos", icon: "mdi-chart-line", link: "/pagos", badge: null, permiso: "reportes_pagos" },
          { title: "Visitas", icon: "mdi-chart-areaspline", link: "/visitas", badge: null, permiso: "reportes_visitas" },
        ]
      },
      {
        title: "Configuración",
        icon: "mdi-cog",
        open: false,
        items: [
          { title: "Información del Gimnasio", icon: "mdi-information", link: "/configurar", badge: null, permiso: "configuracion" },
          { title: "Roles y Permisos", icon: "mdi-shield-account-outline", link: "/roles", badge: null, permiso: "roles" },
          { title: "Mi Perfil", icon: "mdi-account-cog", link: "/perfil", badge: null, permiso: null },
        ]
      },
    ],
  }),

  computed: {
    drawerWidth() {
      return this.$vuetify.breakpoint.smAndDown ? 280 : 320
    },

    // Solo los accesos rápidos que el usuario tiene permiso de ver.
    quickNavItemsFiltrados() {
      return this.quickNavItems.filter(item => this.puedeVer(item.permiso))
    },

    // Cada sección del menú, pero solo con los ítems permitidos — y si
    // una sección se queda sin ningún ítem visible, la sección completa
    // se oculta (no tiene sentido mostrar un encabezado vacío).
    menuSectionsFiltradas() {
      return this.menuSections
        .map(section => ({
          ...section,
          items: section.items.filter(item => this.puedeVer(item.permiso))
        }))
        .filter(section => section.items.length > 0)
    }
  },

  mounted() {
    this.nombreUsuario = localStorage.getItem("usuario") || "Administrador"
    this.nombreGimnasio = localStorage.getItem("nombreGimnasio") || "Mi Gimnasio"
    this.logo = localStorage.getItem("logoGimnasio")

    window.addEventListener('scroll', this.handleScroll)
    this.updateActiveQuickNav()
  },

  beforeDestroy() {
    window.removeEventListener('scroll', this.handleScroll)
  },

  watch: {
    $route() {
      this.updateActiveQuickNav()
    }
  },

  methods: {

    puedeVer(permiso) {
      if (!permiso) return true
      return this.$store.getters.tienePermiso(permiso)
    },

    handleScroll() {
      this.scrolled = window.scrollY > 20
    },

    setActiveQuickNav(title) {
      this.quickNavItems.forEach(item => {
        item.active = item.title === title
      })
    },

    updateActiveQuickNav() {
      const currentRoute = this.$route.path
      this.quickNavItems.forEach(item => {
        item.active = currentRoute === item.link ||
          (item.link === '/' && currentRoute === '/') ||
          (item.link !== '/' && currentRoute.startsWith(item.link))
      })
    },

    salir() {
      this.$store.commit('logout')
      this.$router.push('/login').catch(() => {})
    },
    urlImagen(imagen) {
      return Utiles.generarURL(imagen)
    },

    toggleTheme() {
      this.$vuetify.theme.dark = !this.$vuetify.theme.dark
    },

    toggleDrawer() {
      this.drawer = !this.drawer
    },

    closeDrawer() {
      if (this.$vuetify.breakpoint.smAndDown) {
        this.drawer = false
      }
    }
  }
}
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@500;700&family=Inter:wght@400;500;600&family=JetBrains+Mono:wght@500&display=swap');

/* ===== TOKENS (mismos del sistema) ===== */
.modern-app-bar,
.modern-drawer {
  --bg: #0E0E10;
  --card: #17171A;
  --card-hover: #1B1B1F;
  --card-border: #26262A;
  --text-primary: #F2F2ED;
  --text-secondary: #8C8C93;
  --text-tertiary: #5C5C62;
  --accent: #C8FF4D;
  --accent-dim: rgba(200, 255, 77, 0.12);
  --error: #FF6B6B;
  --radius: 4px;
  --font-display: 'Space Grotesk', sans-serif;
  --font-body: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
  --font-mono: 'JetBrains Mono', monospace;
}

/* ===== APP BAR ===== */
.modern-app-bar {
  background: rgba(14, 14, 16, 0.92) !important;
  backdrop-filter: blur(10px) !important;
  border-bottom: 1px solid var(--card-border) !important;
  transition: border-color 0.2s ease, background 0.2s ease !important;
}

.modern-app-bar.scrolled {
  background: rgba(14, 14, 16, 0.98) !important;
  border-bottom-color: var(--card-border) !important;
}

/* Marca */
.brand-section {
  display: flex;
  align-items: center;
  cursor: pointer;
}

.logo-container {
  display: flex;
  align-items: center;
}

.logo-box {
  width: 34px;
  height: 34px;
  border-radius: var(--radius);
  background: var(--card);
  border: 1px solid var(--card-border);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 12px;
  overflow: hidden;
}

.logo-image {
  width: 65%;
  height: 65%;
  object-fit: contain;
}

.brand-text {
  display: flex;
  flex-direction: column;
}

.gym-name {
  font-family: var(--font-display);
  font-weight: 700;
  font-size: 0.95rem;
  color: var(--text-primary);
  line-height: 1.2;
}

.gym-tagline {
  font-family: var(--font-mono);
  font-size: 0.62rem;
  color: var(--text-tertiary);
  letter-spacing: 0.1em;
  text-transform: uppercase;
  margin-top: 2px;
}

/* Navegación rápida */
.desktop-quick-nav {
  display: flex;
  align-items: center;
  margin-left: 28px;
  gap: 2px;
}

.quick-nav-btn {
  color: var(--text-secondary) !important;
  font-family: var(--font-body) !important;
  font-weight: 500 !important;
  font-size: 0.82rem !important;
  text-transform: none !important;
  letter-spacing: 0 !important;
  border-radius: var(--radius) !important;
  padding: 6px 14px !important;
  min-width: auto !important;
  box-shadow: none !important;
  transition: color 0.15s ease, background 0.15s ease !important;
}

.quick-nav-btn:hover {
  color: var(--text-primary) !important;
  background: rgba(255, 255, 255, 0.04) !important;
}

.quick-nav-btn.active-nav {
  color: var(--text-primary) !important;
  background: var(--card) !important;
  border: 1px solid var(--card-border) !important;
}

/* Acciones de usuario */
.user-actions {
  display: flex;
  align-items: center;
  gap: 4px;
}

.action-btn {
  transition: opacity 0.15s ease !important;
}

.action-btn:hover {
  opacity: 0.7;
}

.user-profile-btn {
  color: var(--text-primary) !important;
  text-transform: none !important;
  font-weight: 500 !important;
  border-radius: var(--radius) !important;
  padding: 6px 10px !important;
  box-shadow: none !important;
}

.user-profile-btn:hover {
  background: rgba(255, 255, 255, 0.04) !important;
}

.user-avatar {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: var(--accent);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin-right: 8px;
}

.username {
  font-family: var(--font-body);
  font-size: 0.82rem;
  color: var(--text-primary);
  margin-right: 6px;
}

.drawer-toggle-btn,
.nav-icon-mobile {
  transition: opacity 0.15s ease !important;
}

.drawer-toggle-btn:hover {
  opacity: 0.7;
}

/* Menús flotantes (notificaciones / perfil) */
.dropdown-card {
  width: 280px;
  border-radius: var(--radius) !important;
  margin-top: 8px;
  background: var(--card) !important;
  border: 1px solid var(--card-border);
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.4) !important;
  overflow: hidden;
  padding: 6px;
}

.dropdown-card-narrow {
  width: 200px;
}

.dropdown-head {
  font-family: var(--font-mono);
  font-size: 10.5px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--text-tertiary);
  padding: 8px 10px 4px;
}

.dropdown-divider {
  border-color: var(--card-border) !important;
  margin: 4px 0;
}

.dropdown-item {
  display: flex;
  align-items: center;
  padding: 8px 10px;
  border-radius: var(--radius);
}

.dropdown-item-title {
  font-size: 12.5px;
  color: var(--text-primary);
}

.dropdown-item-time {
  font-size: 11px;
  color: var(--text-tertiary);
}

.dropdown-link {
  min-height: 38px !important;
  padding: 8px 10px !important;
  border-radius: var(--radius) !important;
  font-size: 13px;
  color: var(--text-secondary) !important;
  transition: background 0.15s ease, color 0.15s ease;
}

.dropdown-link:hover {
  background: rgba(255, 255, 255, 0.04) !important;
  color: var(--text-primary) !important;
}

.dropdown-link-danger:hover {
  background: rgba(255, 107, 107, 0.08) !important;
  color: var(--error) !important;
}

/* ===== DRAWER ===== */
.modern-drawer {
  background: var(--bg) !important;
  border-left: 1px solid var(--card-border) !important;
  box-shadow: -8px 0 32px rgba(0, 0, 0, 0.4) !important;
}

.drawer-header {
  padding: 28px 20px 20px;
  border-bottom: 1px solid var(--card-border);
}

.drawer-user-info {
  display: flex;
  align-items: center;
  gap: 14px;
  margin-bottom: 20px;
}

.drawer-avatar {
  border-radius: var(--radius) !important;
  border: 1px solid var(--card-border);
  background: var(--accent);
  flex-shrink: 0;
}

.gym-name-drawer {
  font-family: var(--font-display);
  color: var(--text-primary);
  font-weight: 700;
  font-size: 1rem;
  margin: 0 0 2px;
  line-height: 1.2;
}

.user-role {
  color: var(--text-secondary);
  font-size: 0.8rem;
  margin-bottom: 6px;
}

.status-pill {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-family: var(--font-mono);
  font-size: 10px;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--text-tertiary);
}

.status-dot {
  width: 5px;
  height: 5px;
  border-radius: 50%;
  background: var(--accent);
  box-shadow: 0 0 6px var(--accent);
}

.quick-actions {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.quick-action-btn {
  display: flex;
  align-items: center;
  width: 100%;
  background: var(--card);
  border: 1px solid var(--card-border);
  border-radius: var(--radius);
  padding: 9px 12px;
  font-family: var(--font-body);
  font-size: 12.5px;
  font-weight: 500;
  color: var(--text-primary);
  cursor: pointer;
  transition: border-color 0.15s ease, background 0.15s ease;
}

.quick-action-btn:hover {
  border-color: var(--text-tertiary);
  background: var(--card-hover);
}

/* Lista de navegación */
.modern-nav-list {
  padding: 12px 12px 0;
  background: transparent !important;
}

.section-divider {
  height: 1px;
  background: var(--card-border);
  margin: 8px 0;
}

.modern-section-group {
  background: transparent !important;
  margin: 2px 0;
}

.modern-section-group >>> .v-list-group__header {
  border-radius: var(--radius) !important;
  padding: 4px 8px !important;
  min-height: 38px;
  transition: background 0.15s ease;
}

.modern-section-group >>> .v-list-group__header:hover {
  background: rgba(255, 255, 255, 0.03) !important;
}

.modern-section-group >>> .v-icon {
  color: var(--text-tertiary) !important;
  font-size: 17px !important;
}

.section-title {
  font-family: var(--font-mono);
  font-weight: 500;
  font-size: 10.5px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--text-secondary) !important;
}

.modern-menu-item {
  border-radius: var(--radius) !important;
  margin: 1px 0 !important;
  padding: 7px 12px !important;
  min-height: 36px;
  transition: background 0.15s ease;
}

.modern-menu-item:hover {
  background: rgba(255, 255, 255, 0.03) !important;
}

.modern-menu-item.active-menu-item {
  background: var(--card) !important;
  border-left: 2px solid var(--accent);
}

.menu-icon {
  width: 24px;
  height: 24px;
  border-radius: var(--radius);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 4px;
  color: var(--text-secondary);
}

.active-menu-item .menu-icon {
  color: var(--accent);
}

.menu-item-title {
  font-family: var(--font-body);
  font-size: 13px;
  color: var(--text-secondary) !important;
  font-weight: 500;
}

.active-menu-item .menu-item-title {
  color: var(--text-primary) !important;
}

.menu-badge {
  font-family: var(--font-mono);
  font-size: 9.5px;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: var(--bg);
  background: var(--accent);
  border-radius: var(--radius);
  padding: 2px 6px;
}

/* Footer del drawer */
.drawer-footer {
  margin-top: auto;
  padding: 16px 20px 20px;
}

.footer-divider {
  height: 1px;
  background: var(--card-border);
  margin-bottom: 14px;
}

.footer-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 14px;
}

.system-text {
  font-family: var(--font-mono);
  font-size: 10.5px;
  color: var(--text-tertiary);
  letter-spacing: 0.03em;
}

.footer-icon-btn {
  width: 28px;
  height: 28px;
  border-radius: var(--radius);
  border: 1px solid var(--card-border);
  background: transparent;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: border-color 0.15s ease;
}

.footer-icon-btn:hover {
  border-color: var(--text-tertiary);
}

.logout-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  background: transparent;
  border: 1px solid var(--card-border);
  border-radius: var(--radius);
  padding: 9px;
  font-family: var(--font-body);
  font-size: 12.5px;
  font-weight: 500;
  color: var(--error);
  cursor: pointer;
  transition: background 0.15s ease, border-color 0.15s ease;
}

.logout-btn:hover {
  background: rgba(255, 107, 107, 0.08);
  border-color: rgba(255, 107, 107, 0.3);
}

/* Scrollbar */
.modern-drawer::-webkit-scrollbar {
  width: 5px;
}

.modern-drawer::-webkit-scrollbar-track {
  background: transparent;
}

.modern-drawer::-webkit-scrollbar-thumb {
  background: var(--card-border);
  border-radius: 3px;
}

/* ===== RESPONSIVE ===== */
@media (max-width: 1264px) {
  .desktop-quick-nav {
    margin-left: 14px;
  }

  .quick-nav-btn {
    padding: 6px 10px !important;
    font-size: 0.78rem !important;
  }
}

@media (max-width: 960px) {

  .desktop-quick-nav,
  .username {
    display: none;
  }
}

@media (max-width: 600px) {
  .brand-text {
    display: none;
  }

  .logo-box {
    width: 32px;
    height: 32px;
    margin-right: 8px;
  }
}

@media (min-width: 960px) {
  .modern-drawer {
    width: 320px !important;
  }
}
</style>


// WEBPACK FOOTER //
// src/components/Encabezado.vue